import React, { ComponentProps } from 'react';

import PropTypes from 'prop-types';

import A from '@adsk/alloy-react-a';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import ErrorPage from '../ErrorPage';

const DefaultAction = ({ homeUrl }: { homeUrl?: string }) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  return (
    <A href={homeUrl} style={theme.typography.heading2}>
      {T.translate('GO_HOME')}
    </A>
  );
};

DefaultAction.defaultProps = {
  homeUrl: '/',
};

DefaultAction.propTypes = {
  homeUrl: PropTypes.string,
};

type PageNotFoundProps = ComponentProps<typeof ErrorPage> & {
  homeUrl?: string;
};

/**
 * A PageNotFound component. Used to show a generic "page not found" error.
 * Can be used as a fallback for unknown routes for example.
 */
const PageNotFound = React.forwardRef<HTMLDivElement, PageNotFoundProps>(
  (
    {
      title,
      description,
      action,
      footer,
      onLogout,
      homeUrl,
      illustrationType = ErrorPage.ILLUSTRATION_TYPES.SKYLINE_GREY,
      ...props
    },
    ref,
  ) => {
    const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
    const finalTitle = title || T.translate('PAGE_NOT_FOUND_TITLE');
    const finalDescription =
      description || T.translate('PAGE_NOT_FOUND_DESCRIPTION');
    const finalAction = action || <DefaultAction homeUrl={homeUrl} />;
    const finalFooter = footer || T.translate('404_ERROR');

    return (
      <ErrorPage
        onLogout={onLogout}
        ref={ref}
        title={finalTitle}
        illustrationType={illustrationType}
        description={finalDescription}
        action={finalAction}
        footer={finalFooter}
        {...props}
      />
    );
  },
);

PageNotFound.displayName = 'PageNotFound';

PageNotFound.propTypes = {
  ...ErrorPage.propTypes,
  homeUrl: PropTypes.string,
};

export default Object.assign(PageNotFound, {
  ILLUSTRATION_TYPES: ErrorPage.ILLUSTRATION_TYPES,
});
